import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle, Compass, Star, Camera, Mountain, Utensils, HelpCircle, Hash, ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";

import API_BASE_URL from "../config";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const ICON_MAP = {
  "message-circle": MessageCircle,
  compass: Compass,
  star: Star,
  camera: Camera,
  mountain: Mountain,
  utensils: Utensils,
  "help-circle": HelpCircle,
};

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/channels`, { withCredentials: true });
        setChannels(data.channels || []);
      } catch (error) {
        console.error("Channels load error:", error);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#f4f2f8' }}>
      <style>{`
        .channel-card {
          background: white;
          border: 1px solid rgba(42,26,74,0.08);
          border-radius: 16px;
          padding: 20px;
          display: block;
          transition: all 0.3s ease;
          height: 100%;
          text-decoration: none;
        }
        .channel-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(42,26,74,0.12);
        }
      `}</style>

      {/* Hero */}
      <div style={{ background: '#2a1a4a', padding: '48px 20px 40px' }}>
        <div style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 700, margin: '0 0 10px' }}>
            Community Channels
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>
            Join the conversation. Share your Uttarakhand travel stories, ask for advice, or connect with fellow explorers.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px 80px' }}>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ borderRadius: '16px', padding: '20px', background: 'white', border: '1px solid rgba(42,26,74,0.08)', animation: 'pulse 1.5s infinite' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '10px', marginBottom: '16px', background: '#e8e0f8' }} />
                <div style={{ height: '20px', borderRadius: '6px', width: '75%', marginBottom: '10px', background: '#e8e0f8' }} />
                <div style={{ height: '14px', borderRadius: '6px', width: '100%', marginBottom: '8px', background: '#e8e0f8' }} />
                <div style={{ height: '14px', borderRadius: '6px', width: '80%', background: '#e8e0f8' }} />
              </div>
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div style={{ borderRadius: '16px', padding: '48px', textAlign: 'center', maxWidth: '480px', margin: '0 auto', background: 'white', border: '1px solid rgba(42,26,74,0.08)' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', background: '#e8e0f8' }}>
              <MessageCircle style={{ width: '32px', height: '32px', color: '#2a1a4a' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: '#2a1a4a' }}>No communities yet</h3>
            <p style={{ marginBottom: '24px', color: '#6b7280', fontSize: '14px' }}>Be the first to start a conversation about Uttarakhand!</p>
            <div style={{ fontSize: '12px' }}>
              <p style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', color: '#9ca3af' }}>Suggested Topics</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                {["Offbeat Rishikesh", "Work from Mountains", "Travel Partners", "Kedarnath Yatra"].map((name) => (
                  <span key={name} style={{ padding: '6px 14px', borderRadius: '999px', fontWeight: 500, background: '#e8e0f8', color: '#2a1a4a', border: '1px solid rgba(42,26,74,0.12)', fontSize: '13px' }}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {channels.map((ch) => {
              const Icon = ICON_MAP[ch.icon] || Hash;
              return (
                <motion.div key={ch.id} variants={fadeUp}>
                  <Link
                    to={`/channels/${ch.id}`}
                    className="channel-card group"
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div
                        style={{ width: '48px', height: '48px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', background: '#e8e0f8', transition: 'transform 0.3s ease' }}
                        className="group-hover:scale-110"
                      >
                        <Icon style={{ width: '24px', height: '24px', color: '#2a1a4a' }} strokeWidth={2} />
                      </div>

                      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#2a1a4a' }}>
                        {ch.name}
                      </h3>

                      <p style={{ marginBottom: '16px', flexGrow: 1, lineHeight: 1.6, color: '#6b7280', fontSize: '14px' }}>
                        {ch.description}
                      </p>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid rgba(42,26,74,0.08)' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', color: '#9ca3af' }}>
                          <MessageCircle style={{ width: '14px', height: '14px' }} />
                          {ch.post_count || 0} posts
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '999px', fontWeight: 600, fontSize: '13px', background: '#2a1a4a', color: 'white' }}>
                          Join
                          <ArrowRight style={{ width: '14px', height: '14px' }} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
